/**
 * https://stackoverflow.com/a/68533063/5423329
 */
export const getEnumKeyByEnumValue = <
  TEnumKey extends string,
  TEnumVal extends string | number
>(
  myEnum: { [key in TEnumKey]: TEnumVal },
  enumValue: TEnumVal
): TEnumKey => {
  const keys = (Object.keys(myEnum) as TEnumKey[]).filter(
    (x) => myEnum[x] === enumValue
  );
  if (keys.length !== 1) {
    throw new Error(
      `Could not find matching key in enum ${myEnum} for value ${enumValue}`
    );
  }
  return keys[0];
};

/**
 * WARNING: This does not provide type safety
 * @param obj Object to filter
 * @param type Object by whose keys to filter
 * @returns Object filtered to only those properties on `type`
 */
export const filterPropsToThoseOnType = (obj: object, type: object): object => {
  const keys: string[] = getKeysInCommon(obj, type);
  return filterObjectPropertiesToGivenKeys(obj, keys);
};

const getKeysInCommon = (obj1: object, obj2: object): string[] => {
  return Object.keys(obj1).filter((key) => {
    return Object.keys(obj2).includes(key);
  });
};

const filterObjectPropertiesToGivenKeys = (obj: object, keys: string[]): object => {
  return Object.fromEntries(Object.entries(obj).filter(([key, _]) => keys.includes(key)));
};

/**
 * Some console.log statements were returning [object Object] rather than the actual object values.
 * Due to this I converted all the objects logged to Json.stringify versions.
 * That caused issues when the error was just a string and not a full object literal.
 *
 * This function attempts to get the best of both worlds by printing out primitives as they are and objects as stringified versions
 */
export const logFriendlyObject = (toPrint: any): string => {
  //based off this: https://codedamn.com/news/javascript/how-to-fix-typeerror-converting-circular-structure-to-json-in-js
  const stringify = (obj: any) => {
    const cache: any[] = [];
    const str = JSON.stringify(obj, (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (cache.indexOf(value) !== -1) {
          // Circular reference found, discard key
          return;
        }
        if (key.startsWith('__reactFiber')) {
          // Avoid trying to stringify React components recursively
          return `[${value.type} component]`;
        }
        if (key.startsWith('__reactProps')) {
          // Avoid trying to stringify React props
          return `[props]`;
        }
        if (key.startsWith('__events')) {
          // Avoid trying to stringify events
          return `[events]`;
        }

        // Store value in our collection
        cache.push(value);
      }
      return value;
    }, 2);
    return str;
  }

  if (typeof toPrint === 'string' || typeof toPrint === 'number' || typeof toPrint === 'boolean') return '' + toPrint;

  //Map and Set support from https://codebuckets.com/2022/09/30/console_log-for-map-and-set/
  if (toPrint instanceof Set || toPrint instanceof Map) {
    const asArray = Array.from(toPrint);
    return logFriendlyObject(asArray);
  }

  try {
    const jsonVersion = stringify(toPrint);
    if (jsonVersion === '{ }' || jsonVersion === '{}') {
      return 'empty object (' + toPrint + ')';
    }
    return jsonVersion;
  } catch (e) {
    console.warn('When tying to make an object log friendly we encountered an error', toPrint, e);
  }
  return toPrint;
};
